import { forwardRef, useEffect, useImperativeHandle, useRef, type InputHTMLAttributes, type ReactNode } from 'react';
import { Check, Minus } from '@phosphor-icons/react';
import { cx } from '../cx';
import { Icon } from '../Icon';

export type CheckboxProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange'> & {
  label: ReactNode;
  description?: ReactNode;
  checked?: boolean;
  /** Some, not all, of a group is checked (shows a dash; reads as "mixed"). */
  indeterminate?: boolean;
  onCheckedChange?: (checked: boolean) => void;
};

/**
 * A native checkbox, restyled: 3:1 outline, fills with the selected colour when checked. For forms
 * and settings; filters use FilterChip instead.
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, description, checked, indeterminate = false, onCheckedChange, disabled, className, ...rest },
  ref,
) {
  const inner = useRef<HTMLInputElement>(null);
  useImperativeHandle(ref, () => inner.current as HTMLInputElement);
  useEffect(() => {
    if (inner.current) inner.current.indeterminate = indeterminate;
  }, [indeterminate]);

  return (
    <label
      className={cx(
        'inline-flex items-start gap-3 text-sm text-jc-ink',
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
        className,
      )}
    >
      <span className="relative mt-px grid size-5.5 shrink-0 place-items-center">
        <input
          {...rest}
          ref={inner}
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onCheckedChange?.(e.target.checked)}
          className={cx(
            'peer absolute inset-0 m-0 cursor-pointer appearance-none disabled:cursor-not-allowed rounded-jc-info border border-jc-border-control bg-jc-surface',
            'transition-colors duration-(--jc-duration-fast) checked:border-jc-selected checked:bg-jc-selected indeterminate:border-jc-selected indeterminate:bg-jc-selected',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jc-focus focus-visible:ring-offset-2 focus-visible:ring-offset-jc-page',
          )}
        />
        <span className="pointer-events-none relative text-jc-on-selected opacity-0 peer-checked:opacity-100 peer-indeterminate:opacity-100">
          <Icon icon={indeterminate ? Minus : Check} size="sm" />
        </span>
      </span>
      <span className="flex flex-col gap-0.5">
        <span>{label}</span>
        {description ? <span className="text-sm text-jc-ink-secondary">{description}</span> : null}
      </span>
    </label>
  );
});
